import 'regenerator-runtime/runtime';
import { call, select, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
  ProduktRequested,
  ProdukttabelleEnum,
} from '@ps20-frontend/shop-integration-redux';
import { PS20Logger } from '@ps20-frontend/logging';
import { App } from '@ps20-frontend/frontend-models';
import { MasterProduct } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../localStore';
import { buildProdukt } from './helpers/produktDataMapper';

export enum PRODUKTE_REQUESTED_EVENTS {
  PRODUKTE_REQUESTED_SUCCESSFUL = 'PRODUKTE_REQUESTED_SUCCESSFUL',
  PRODUKTE_REQUESTED_SUCCESSFUL_AFTER_SAVE = 'PRODUKTE_REQUESTED_SUCCESSFUL_AFTER_SAVE',
  PRODUKTE_REQUESTED_FAILED = 'PRODUKTE_REQUESTED_FAILED',
  PRODUKTE_REQUESTED_FAILED_AFTER_SAVE = 'PRODUKTE_REQUESTED_FAILED_AFTER_SAVE',
}

export function* watchMasterProduktSaga() {
  yield takeEvery(GlobalActions.PRODUKT_REQUESTED, safeSaga(masterProduktSaga, App.SHOP));
  yield takeEvery(
    GlobalActions.PRODUKT_REQUESTED_AFTER_SAVE,
    safeSaga(masterProduktSagaAfterSave, App.SHOP),
  );
  yield takeEvery(
    PRODUKTE_REQUESTED_EVENTS.PRODUKTE_REQUESTED_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP),
  );
  yield takeEvery(
    PRODUKTE_REQUESTED_EVENTS.PRODUKTE_REQUESTED_SUCCESSFUL_AFTER_SAVE,
    safeSaga(successCallbackAfterSave, App.SHOP),
  );
}

export function* masterProduktSagaAfterSave(action: GlobalAction<ProduktRequested>) {
  const globalPut = GlobalPut.getInstance();
  const { appId, angebotsId, produkttabelle } = action.payload;
  yield call(
    globalPut.put,
    new GlobalActions().doRequest({
      appId,
      angebotsId,
      url: getURLofAppId(appId, produkttabelle),
      method: HTTP_METHOD.GET,
      additionalData: { produkttabelle },
      successEvent: PRODUKTE_REQUESTED_EVENTS.PRODUKTE_REQUESTED_SUCCESSFUL_AFTER_SAVE,
      errorEvent: PRODUKTE_REQUESTED_EVENTS.PRODUKTE_REQUESTED_FAILED_AFTER_SAVE,
    }),
  );
}

export function* masterProduktSaga(action: GlobalAction<ProduktRequested>) {
  const globalPut = GlobalPut.getInstance();
  const { appId, angebotsId, produkttabelle, catalogsOnly } = action.payload;
  if (catalogsOnly) {
    return;
  }
  const url = getURLofAppId(appId, produkttabelle);
  PS20Logger.info(
    'MasterProdukt.requested',
    JSON.stringify({ appId, angebotsId, produkttabelle, url }),
    App.SHOP,
  );
  yield call(
    globalPut.put,
    new GlobalActions().doRequest({
      appId,
      angebotsId,
      url,
      method: HTTP_METHOD.GET,
      additionalData: { produkttabelle },
      successEvent: PRODUKTE_REQUESTED_EVENTS.PRODUKTE_REQUESTED_SUCCESSFUL,
      errorEvent: PRODUKTE_REQUESTED_EVENTS.PRODUKTE_REQUESTED_FAILED,
    }),
  );
}

export function* successCallbackAfterSave(successAction: DoRequestResponse<MasterProduct>) {
  const state = (yield select()) as IAppState;
  try {
    const produkt = buildProdukt(successAction.payload, state);
    const globalPut = GlobalPut.getInstance();
    const { appId, angebotsId, additionalData } = successAction.doRequestAction.payload;
    const produkttabelle = (additionalData as { produkttabelle?: ProdukttabelleEnum })
      ?.produkttabelle;
    const produktReceivedAction = new GlobalActions().produktReceivedAfterSave({
      appId,
      produktData: produkt,
      angebotsId,
      produkttabelle,
    });
    yield call(globalPut.put, produktReceivedAction);
  } catch (error) {
    PS20Logger.error(
      App.SHOP,
      error as Error,
      'invalid response data, MasterProduktSaga.successCallback(), payload: ' +
        successAction.doRequestAction.payload,
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<MasterProduct>) {
  const state = (yield select()) as IAppState;
  try {
    const produkt = buildProdukt(successAction.payload, state);
    const globalPut = GlobalPut.getInstance();
    const { appId, angebotsId, additionalData } = successAction.doRequestAction.payload;
    const produkttabelle = (additionalData as { produkttabelle?: ProdukttabelleEnum })
      ?.produkttabelle;
    const produktReceivedAction = new GlobalActions().produktReceived({
      appId,
      produktData: produkt,
      angebotsId,
      produkttabelle,
    });
    yield call(globalPut.put, produktReceivedAction);
  } catch (error) {
    PS20Logger.error(
      App.SHOP,
      error as Error,
      'invalid response data, MasterProduktSaga.successCallback(), payload: ' +
        successAction.doRequestAction.payload,
    );
  }
}

function getURLofAppId(appId: App, produkttabelle: ProdukttabelleEnum): string {
  const urlMapping: Record<string, string> = {
    // RS
    [`${App.RS}_${ProdukttabelleEnum.RS_VERKEHR}`]: '/privatschutz/api/masterprodukt/rs-verkehr',

    // TKV
    [`${App.TKV}_${ProdukttabelleEnum.TKV_HUND_KATZE}`]:
      '/privatschutz/api/masterprodukt/tkv-hund-katze',
    [`${App.TKV}_${ProdukttabelleEnum.TKV_PFERD}`]: '/privatschutz/api/masterprodukt/tkv-pferd',

    // TKV_V2
    [`${App.TKV_V2}_${ProdukttabelleEnum.TKV_V2_HUND_OPSCHUTZ}`]:
      '/privatschutz/api/masterprodukt/tkv-hund-opschutz',
    [`${App.TKV_V2}_${ProdukttabelleEnum.TKV_V2_KATZE_OPSCHUTZ}`]:
      '/privatschutz/api/masterprodukt/tkv-katze-opschutz',
    [`${App.TKV_V2}_${ProdukttabelleEnum.TKV_V2_PFERD_OPSCHUTZ}`]:
      '/privatschutz/api/masterprodukt/tkv-pferd-opschutz',
    [`${App.TKV_V2}_${ProdukttabelleEnum.TKV_V2_HUND_VOLLSCHUTZ}`]:
      '/privatschutz/api/masterprodukt/tkv-hund-vollschutz',
    [`${App.TKV_V2}_${ProdukttabelleEnum.TKV_V2_KATZE_VOLLSCHUTZ}`]:
      '/privatschutz/api/masterprodukt/tkv-katze-vollschutz',
    [`${App.TKV_V2}_${ProdukttabelleEnum.TKV_V2_PFERD_VOLLSCHUTZ}`]:
      '/privatschutz/api/masterprodukt/tkv-pferd-vollschutz',

    // Other Apps
    [App.VWG]: '/privatschutz/api/masterprodukt/vwg',
    [App.VWG_V2]: '/privatschutz/api/masterprodukt/vwg_v2',
    [App.VHV]: '/privatschutz/api/masterprodukt/vhv',
    [App.VHV_V2]: '/privatschutz/api/masterprodukt/vhv_v2',
    [App.PH]: '/privatschutz/api/masterprodukt/ph',
    [App.JHP]: '/privatschutz/api/masterprodukt/jhp',
    [App.RS]: '/privatschutz/api/masterprodukt/rs',
    [App.RS_VERMIETUNG]: '/privatschutz/api/masterprodukt/rs-vermietung',
    [App.THH_HUND]: '/privatschutz/api/masterprodukt/thh-hund',
    [App.THH_PFERD]: '/privatschutz/api/masterprodukt/thh-pferd',
    [App.TKV]: '/privatschutz/api/masterprodukt/tkv-hund-katze',
    [App.UNF]: '/privatschutz/api/masterprodukt/uri',
    [App.GEGE]: '/privatschutz/api/masterprodukt/gege',
  };

  const compositeKey = `${appId}_${produkttabelle}`;
  const url = urlMapping[compositeKey] ?? urlMapping[appId];

  if (!url) {
    throw new Error('No Produkt found for: ' + appId);
  }

  return url;
}
